/*
 * 背景：诊断记录里的 flagged_tile_boxes 是**互相重叠**的：分块边长 128、步长 64，
 *   所以每一格与四邻各叠一半。把 44 个框各自描边画出来，屏幕上是一团交叉的细线，
 *   数不出有几格，也看不出它们连成一片——而「连成一片」正是这个现象的特征。
 * 设计意图：先把这些框合成一个区域，再只画这个区域的**外轮廓**与一层淡填充。
 *   合并靠把框铺到公共网格上：所有坐标都是步长的整数倍，
 *   于是以坐标的最大公约数为格边长时，每个框都精确落在若干个格子上，没有半格。
 * 关键约束：
 *   1. 只用诊断记录里的坐标，不做取整对齐之外的任何修饰。红框位置必须能与产物对上，
 *      画歪了就是在编造「问题出在哪」。
 *   2. 输出的是 SVG 路径，坐标沿用诊断的画布尺寸（measured_on）。缩放交给 viewBox，
 *      在 JS 里换算成显示像素会与图片的实际显示尺寸脱钩，图一缩放红框就飘。
 */

/** 一片标记区域的两条路径：fill 是区域本身，edge 是它的外轮廓。 */
export interface TileMarksPath {
  fill: string
  edge: string
  cell: number
}

const gcd2 = (a: number, b: number): number => (b ? gcd2(b, a % b) : a)

/**
 * 网格边长：所有框坐标的最大公约数。
 *
 * 实测两种情形都落在 64（分块 128、步长 64）与 128（只有孤零一格时）上。
 * 万一以后管线换成非整数或互素的坐标，gcd 会退到很小的值，格子数随之爆掉，
 * 所以给一个上限，超了就成倍放粗。放粗只会让轮廓略微外扩，不会漏掉标记区域。
 */
function gridCell(boxes: readonly (readonly number[])[]): number {
  let g = 0
  for (const b of boxes) for (const v of b) g = gcd2(g, Math.abs(Math.round(v)))
  let cell = g || 1
  const area = boxes.reduce((s, b) => s + (b[2] - b[0]) * (b[3] - b[1]), 0)
  while (area / (cell * cell) > 20000) cell *= 2
  return cell
}

/**
 * 把一组（可能重叠的）分块框合成「一片区域 + 外轮廓」两条 SVG 路径。
 *
 * 轮廓只画覆盖格与空白格之间的那些边，所以区域内部不留线；
 * 填充由互不重叠的格子拼成，于是半透明红不会在重叠处叠深，整片浓度一致。
 */
export function tileMarks(boxes: readonly (readonly number[])[]): TileMarksPath | null {
  if (!boxes.length) return null
  const cell = gridCell(boxes)
  const on = new Set<string>()
  for (const b of boxes) {
    const [x0, y0, x1, y1] = b
    for (let y = Math.round(y0); y < y1; y += cell) {
      for (let x = Math.round(x0); x < x1; x += cell) on.add(`${x},${y}`)
    }
  }

  const fill: string[] = []
  const edge: string[] = []
  for (const key of on) {
    const [x, y] = key.split(',').map(Number)
    fill.push(`M${x} ${y}h${cell}v${cell}h${-cell}Z`)
    if (!on.has(`${x},${y - cell}`)) edge.push(`M${x} ${y}h${cell}`)
    if (!on.has(`${x},${y + cell}`)) edge.push(`M${x} ${y + cell}h${cell}`)
    if (!on.has(`${x - cell},${y}`)) edge.push(`M${x} ${y}v${cell}`)
    if (!on.has(`${x + cell},${y}`)) edge.push(`M${x + cell} ${y}v${cell}`)
  }
  return { fill: fill.join(''), edge: edge.join(''), cell }
}

/**
 * 同一组框，按「连成一片」拆开，大的排在前面。
 *
 * 背景：2026-09-11 用户裁决「大框应该在底下，不然小框鼠标移动过去看不到小框的提示」。
 *   合成一条路径时，整幅的标记只是一个元素：鼠标压在孤零零那一小块上，浏览器认得的
 *   仍是那一整条路径，于是提示只能按「整片」说，也只能贴着整片摆——那一小块自己
 *   说不上话。
 * 设计意图：一片一片各自成路径，页面按这个顺序画：大的在底下，小的压在上面。
 *   于是鼠标落在哪一片，命中的就是哪一片，提示也就能贴着那一片的上沿摆。
 * 关键约束：拆开只是分组，格子还是同一批格子——合起来与 tileMarks 画出来的一模一样，
 *   红的位置、浓淡、轮廓都不变。分组按四邻接：只在边相连才算一片，斜着挨着不算，
 *   否则两片只碰一个角也会被并成一片，压在上面的就又变回大的那片。
 */
export function tileMarkParts(boxes: readonly (readonly number[])[]): TileMarksPath[] {
  if (!boxes.length) return []
  const cell = gridCell(boxes)
  const on = new Set<string>()
  for (const b of boxes) {
    const [x0, y0, x1, y1] = b
    for (let y = Math.round(y0); y < y1; y += cell) {
      for (let x = Math.round(x0); x < x1; x += cell) on.add(`${x},${y}`)
    }
  }

  const left = new Set(on)
  const groups: string[][] = []
  while (left.size) {
    const first: string = left.values().next().value!
    left.delete(first)
    const group = [first]
    const queue = [first]
    while (queue.length) {
      const [x, y] = queue.pop()!.split(',').map(Number)
      for (const k of [`${x},${y - cell}`, `${x},${y + cell}`,
                       `${x - cell},${y}`, `${x + cell},${y}`]) {
        if (left.delete(k)) {
          group.push(k)
          queue.push(k)
        }
      }
    }
    groups.push(group)
  }

  return groups
    .sort((a, b) => b.length - a.length)
    .map(group => {
      const set = new Set(group)
      const fill: string[] = []
      const edge: string[] = []
      for (const key of group) {
        const [x, y] = key.split(',').map(Number)
        fill.push(`M${x} ${y}h${cell}v${cell}h${-cell}Z`)
        if (!set.has(`${x},${y - cell}`)) edge.push(`M${x} ${y}h${cell}`)
        if (!set.has(`${x},${y + cell}`)) edge.push(`M${x} ${y + cell}h${cell}`)
        if (!set.has(`${x - cell},${y}`)) edge.push(`M${x} ${y}v${cell}`)
        if (!set.has(`${x + cell},${y}`)) edge.push(`M${x + cell} ${y}v${cell}`)
      }
      return { fill: fill.join(''), edge: edge.join(''), cell }
    })
}

/**
 * 诊断读数所在的画布尺寸。
 *
 * 叠瓦判级前管线把画布归一到长边 1280 再分块，所以框的坐标在归一画布上，
 * 不在原始像素上。没有 measured_on 的是归一化修复之前的运行，那时就是在原图上量的。
 */
export function marksCanvas(
  measuredOn: string | undefined,
  pixels: readonly [number, number],
): [number, number] {
  if (measuredOn) {
    const [w, h] = measuredOn.split('x').map(Number)
    if (w > 0 && h > 0) return [w, h]
  }
  return [pixels[0], pixels[1]]
}
